import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

const CRAFTS_URL =
  'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=6eae6326-7571-4054-aa6d-18f239f50dff';
const PEACOCK =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2026/04/white-peacock.png';
/* Same wide witches shot as the trail banner — works as full-bleed */
const HERO_IMG =
  'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp';
const CARD_IMG = 'https://BelvoirCastle.b-cdn.net/Halloween/crafts.webp';
const IMG_CRAFTS =
  'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.25.55.webp';
const IMG_CRAFTS_2 =
  'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.29.45.webp';
const IMG_CRAFTS_3 =
  'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.38.20.webp';
const TRAIL_IMG =
  'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp';

const H = {
  ink: '#14101F',
  midnight: '#1C1630',
  torch: '#E07A2F',
  candle: '#C9A227',
  moss: '#3D5C4A',
  plum: '#5C3A4E',
  bone: '#F6F0E6',
  parchment: '#EDE6D4',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${H.bone};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

/* Full-bleed hero — uses a wider crafts scene (not the card tile crop) */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(560px, 78vh, 820px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: auto;
    min-height: clamp(560px, 82svh, 720px);
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 55%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(20, 16, 31, 0.94) 0%,
    rgba(20, 16, 31, 0.5) 40%,
    rgba(20, 16, 31, 0.18) 68%,
    transparent 100%
  );
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.12;
  background-image:
    repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(224, 122, 47, 0.4) 38px, rgba(224, 122, 47, 0.4) 40px),
    repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(92, 58, 78, 0.5) 38px, rgba(92, 58, 78, 0.5) 40px);
  pointer-events: none;
  mask-image: linear-gradient(to top, black 0%, transparent 45%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(1.75rem, 4.5vw, 3rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
  animation: ${fadeUp} 0.7s ease both;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${H.torch};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.55rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.4rem, 5.8vw, 4.2rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;
  text-shadow: 0 2px 28px rgba(20, 16, 31, 0.65);

  em {
    font-style: italic;
    color: ${H.candle};
  }
`;

const HeroStrapline = styled.p`
  margin: 0 0 1.15rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.35rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  max-width: 34rem;
`;

const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 1.15rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => (p.$fill ? H.torch : 'rgba(20, 16, 31, 0.55)')};
  color: #fff;
  border: 1px solid ${p => (p.$fill ? 'transparent' : 'rgba(255, 255, 255, 0.35)')};
  backdrop-filter: blur(10px);
`;

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;

    a, button {
      width: 100%;
    }
  }
`;

const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: ${H.torch};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #c96a24;
    transform: translateY(-1px);
  }
`;

const BtnGhost = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => (p.$border ? '1px solid rgba(20,16,31,0.07)' : 'none')};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${p => p.$color || H.torch};
`;

const Divider = styled.div`
  width: 2.5rem;
  height: 2px;
  background: ${H.torch};
  margin-bottom: 1.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${p => (p.$light ? '#fff' : H.ink)};

  em {
    font-style: italic;
    color: ${H.torch};
  }
`;

const SectionIntro = styled.p`
  margin: 0 0 1.25rem;
  max-width: 38rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  line-height: 1.65;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.7)' : 'rgba(20,16,31,0.68)')};
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.75rem, 4vw, 3.5rem);
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(20, 16, 31, 0.08);
  border-radius: 12px;
  overflow: hidden;
  margin-top: clamp(1.5rem, 3vw, 2.5rem);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  padding: clamp(1.4rem, 2.8vw, 2rem);
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${H.ink};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  line-height: 1.55;
  color: rgba(20, 16, 31, 0.62);
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 160px 160px;
  gap: 0.55rem;

  @media (max-width: 800px) {
    grid-template-rows: 150px 150px;
  }
`;

const GalleryItem = styled.div`
  border-radius: 10px;
  overflow: hidden;
  grid-column: ${p => (p.$span2 ? 'span 2' : 'span 1')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const PriceStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  margin-top: clamp(1.75rem, 3.5vw, 2.5rem);
  padding: clamp(1.25rem, 2.5vw, 1.75rem) clamp(1.35rem, 3vw, 2rem);
  background: ${H.midnight};
  border-radius: 12px;
`;

const PriceText = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.25rem, 2.5vw, 1.65rem);
  font-weight: 600;
  color: #fff;
  line-height: 1.25;

  em {
    font-style: italic;
    color: ${H.candle};
  }
`;

const PriceBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.6rem;
  background: ${H.torch};
  color: #fff;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease;

  &:hover {
    background: #c96a24;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1px;
  background: rgba(20, 16, 31, 0.08);
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const InfoLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(20, 16, 31, 0.4);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${H.ink};
  line-height: 1.25;
`;

const TrailPanel = styled.section`
  position: relative;
  overflow: hidden;
  background: ${H.ink};
  padding: clamp(3.5rem, 7vw, 5rem) clamp(1.25rem, 5vw, 3.5rem);
`;

const TrailPanelBg = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(105deg, rgba(20, 16, 31, 0.92) 0%, rgba(20, 16, 31, 0.72) 48%, rgba(20, 16, 31, 0.45) 100%),
    url(${TRAIL_IMG}) center / cover no-repeat;
`;

const TrailPanelInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
  max-width: 40rem;
`;

const TrailPanelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.25rem;
  padding: 0.9rem 1.75rem;
  background: ${H.torch};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #c96a24;
  }
`;

const CtaSection = styled.section`
  background: ${H.ink};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(224, 122, 47, 0.05) 38px, rgba(224, 122, 47, 0.05) 40px),
      repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(92, 58, 78, 0.08) 38px, rgba(92, 58, 78, 0.08) 40px);
    pointer-events: none;
  }
`;

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em {
    font-style: italic;
    color: ${H.candle};
  }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.6;
`;

const CtaBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.95rem 2rem;
  background: ${H.torch};
  color: #fff;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  transition: background 0.2s ease;

  &:hover {
    background: #c96a24;
  }
`;

const CtaMeta = styled.p`
  margin: 1.5rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.4);
`;

const HalloweenCrafts = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Halloween Crafts at Belvoir Castle — £4.50 Retail Village Sessions | October 2026</title>
        <meta
          name="description"
          content="Hands-on Halloween crafts at Belvoir Castle Retail Village — only £4.50. Standalone sessions or add to your Enchanted Halloween Trail visit. Take the magic home. October school holidays 2026."
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={getCanonicalUrl('halloween-crafts')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Halloween Crafts at Belvoir Castle — £4.50" />
        <meta
          property="og:description"
          content="Retail Village Halloween craft sessions — £4.50. Standalone or add to the Enchanted Halloween Trail. Take your creation home."
        />
        <meta property="og:url" content={getCanonicalUrl('halloween-crafts')} />
        <meta property="og:image" content={CARD_IMG} />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Halloween Crafts at Belvoir Castle — £4.50" />
        <meta
          name="twitter:description"
          content="Hands-on Halloween crafts in the Retail Village. £4.50. Take the magic home."
        />
        <meta name="twitter:image" content={CARD_IMG} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: 'Halloween Crafts at Belvoir Castle',
            description:
              'Hands-on Halloween craft sessions in the Retail Village at Belvoir Castle. £4.50 per craft. Book as a standalone visit or add to Gardens entry for the Enchanted Halloween Trail.',
            image: [CARD_IMG, IMG_CRAFTS, IMG_CRAFTS_2, IMG_CRAFTS_3, HERO_IMG],
            url: getCanonicalUrl('halloween-crafts'),
            startDate: '2026-10-01',
            endDate: '2026-10-31',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            location: {
              '@type': 'Place',
              name: 'Belvoir Castle Retail Village',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                postalCode: 'NG32 1PE',
                addressRegion: 'Leicestershire',
                addressCountry: 'GB',
              },
            },
            organizer: {
              '@type': 'Organization',
              name: 'Belvoir Castle',
              url: 'https://www.belvoircastle.com',
            },
            offers: {
              '@type': 'Offer',
              url: CRAFTS_URL,
              price: '4.50',
              priceCurrency: 'GBP',
              availability: 'https://schema.org/InStock',
              validFrom: '2026-08-01',
              priceValidUntil: '2026-10-31',
              description: 'Halloween craft session — £4.50. No Gardens ticket required for standalone bookings.',
            },
            audience: { '@type': 'Audience', audienceType: 'Families with children' },
          })}
        </script>
      </Helmet>

      <Page>
        <HeroWrap>
          <HeroImg
            src={HERO_IMG}
            alt="Pesky witches on the adventure playground — Halloween at Belvoir Castle"
          />
          <HeroOverlay />
          <HeroPattern />
          <HeroContent>
            <HeroKicker>Retail Village · October School Holidays 2026</HeroKicker>
            <HeroTitle>
              Halloween <em>Crafts</em>
            </HeroTitle>
            <HeroStrapline>
              Hands-on sessions in the Retail Village — make something magical, then take it home.
            </HeroStrapline>
            <HeroBadgeRow>
              <Badge $fill>£4.50</Badge>
              <Badge>Standalone or with the trail</Badge>
              <Badge>Take it home</Badge>
            </HeroBadgeRow>
            <HeroButtons>
              <BtnPrimary href={CRAFTS_URL} target="_blank" rel="noopener noreferrer">
                Book Crafts — £4.50
              </BtnPrimary>
              <BtnGhost
                type="button"
                onClick={() =>
                  document.getElementById('crafts-about')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                Discover Crafts
              </BtnGhost>
            </HeroButtons>
          </HeroContent>
        </HeroWrap>

        <Section $bg={H.bone} $border id="crafts-about">
          <Inner>
            <TwoCols>
              <div>
                <Kicker $color={H.plum}>Make · Create · Take Home</Kicker>
                <Divider />
                <SectionTitle>
                  Retail Village crafts.<br />
                  <em>Take the magic home.</em>
                </SectionTitle>
                <SectionIntro>
                  Hands-on crafts at the craft tables in the Retail Village.
                  Sessions are open to everyone — no Gardens ticket needed if you&rsquo;re booking crafts
                  on their own.
                </SectionIntro>
                <SectionIntro style={{ marginBottom: 0 }}>
                  Visiting for Belvoir&rsquo;s Enchanted Halloween Trail? The trail ends at the cauldron in
                  the Retail Village — and that&rsquo;s where the craft tables begin. Add crafts to your day,
                  or book them as a standalone treat.
                </SectionIntro>
              </div>
              <GalleryGrid>
                <GalleryItem>
                  <img
                    src={CARD_IMG}
                    alt="Children in Halloween costumes crafting and carving pumpkins at Belvoir"
                  />
                </GalleryItem>
                <GalleryItem>
                  <img
                    src={IMG_CRAFTS}
                    alt="Families decorating pumpkins at a Halloween craft session at Belvoir"
                  />
                </GalleryItem>
                <GalleryItem>
                  <img
                    src={IMG_CRAFTS_2}
                    alt="A child painting a pumpkin at a Halloween craft session at Belvoir"
                  />
                </GalleryItem>
                <GalleryItem>
                  <img
                    src={IMG_CRAFTS_3}
                    alt="Children carving pumpkins at a Halloween craft session at Belvoir"
                  />
                </GalleryItem>
              </GalleryGrid>
            </TwoCols>

            <FeatureGrid>
              <FeatureCard>
                <FeatureTitle>Hands-on making</FeatureTitle>
                <FeatureBody>
                  Seasonal Halloween crafts designed for families — creative, messy in the best way, and
                  properly festive.
                </FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>£4.50 per craft</FeatureTitle>
                <FeatureBody>
                  Simple pricing. Book online ahead of popular half-term dates, or ask at the cauldron on
                  the day if spaces remain.
                </FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>Open to all</FeatureTitle>
                <FeatureBody>
                  Book as a standalone Retail Village visit, or add crafts alongside Gardens entry for the
                  Enchanted Halloween Trail.
                </FeatureBody>
              </FeatureCard>
            </FeatureGrid>

            <PriceStrip>
              <PriceText>
                Halloween crafts — only <em>£4.50</em>
              </PriceText>
              <PriceBtn href={CRAFTS_URL} target="_blank" rel="noopener noreferrer">
                Book Now
              </PriceBtn>
            </PriceStrip>
          </Inner>
        </Section>

        <Section $bg={H.parchment} $border>
          <Inner>
            <Kicker>Plan Your Visit</Kicker>
            <Divider />
            <SectionTitle>
              Essential <em>Information</em>
            </SectionTitle>
            <InfoGrid style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
              <InfoItem>
                <InfoLabel>When</InfoLabel>
                <InfoValue>
                  October School
                  <br />
                  Holidays 2026
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Price</InfoLabel>
                <InfoValue>
                  £4.50
                  <br />
                  per craft
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Where</InfoLabel>
                <InfoValue>
                  Retail Village
                  <br />
                  craft tables
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Entry</InfoLabel>
                <InfoValue>
                  Standalone OK
                  <br />
                  or with Gardens
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Group Saver</InfoLabel>
                <InfoValue>
                  Not applied
                  <br />
                  to crafts
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Location</InfoLabel>
                <InfoValue>
                  Belvoir Castle
                  <br />
                  NG32 1PE
                </InfoValue>
              </InfoItem>
            </InfoGrid>
            <SectionIntro style={{ marginBottom: 0 }}>
              Group Saver applies to Gardens tickets only — not to standalone crafts bookings. Pair crafts
              with the Enchanted Halloween Trail for a full October day out.
            </SectionIntro>
          </Inner>
        </Section>

        <TrailPanel aria-label="Also this October — Belvoir's Enchanted Halloween Trail">
          <TrailPanelBg aria-hidden="true" />
          <TrailPanelInner>
            <Kicker $color={H.torch}>Also this October</Kicker>
            <Divider />
            <SectionTitle $light>
              A pesky witch has cursed Belvoir.
              <br />
              <em>Can you break it?</em>
            </SectionTitle>
            <SectionIntro $light>
              Collect spell ingredients on the adventure playground, find the password animal in the Animal
              Park, and brew the counter-curse at the cauldron — right where the craft tables begin. Silly,
              never scary. Animal Park included with every Gardens ticket.
            </SectionIntro>
            <TrailPanelBtn type="button" onClick={() => navigate('/halloween-belvoir-castle')}>
              Discover the Enchanted Halloween Trail
            </TrailPanelBtn>
          </TrailPanelInner>
        </TrailPanel>

        <CtaSection>
          <CtaInner>
            <img
              src={PEACOCK}
              alt=""
              style={{ height: 52, width: 'auto', margin: '0 auto 1.5rem', display: 'block', opacity: 0.9 }}
            />
            <CtaTitle>
              Make something magical.
              <br />
              <em>Take it home.</em>
            </CtaTitle>
            <CtaBody>
              Book Halloween crafts online for £4.50 — standalone, or as the perfect finish to Belvoir&rsquo;s
              Enchanted Halloween Trail.
            </CtaBody>
            <CtaBtn href={CRAFTS_URL} target="_blank" rel="noopener noreferrer">
              Book Crafts — £4.50
            </CtaBtn>
            <CtaMeta>
              October school holidays 2026 &nbsp;·&nbsp; Retail Village &nbsp;·&nbsp; Silly, never scary
            </CtaMeta>
          </CtaInner>
        </CtaSection>

        <Footer />
      </Page>
    </>
  );
};

export default HalloweenCrafts;
